import { z } from 'zod';

// ---------------------------------------------------------------------------
// Org usage counters + estimated monthly running cost. The cost model applies
// a multiplier to an infrastructure-derived rate card — the basis of future
// usage billing. See docs/CONTRACTS.md.
// ---------------------------------------------------------------------------

export const OrgUsageCountsSchema = z.object({
  members: z.number().int(),
  evidenceFiles: z.number().int(),
  evidenceBytes: z.number().int(),
  corpusItems: z.number().int(),
  documentVersions: z.number().int(),
  assessments: z.number().int(),
  standardsInUse: z.number().int(),
  exportsGenerated: z.number().int(),
  aiJobs: z.number().int(),
  coursesStarted: z.number().int(),
  coursesCompleted: z.number().int(),
  importJobs: z.number().int(),
  /** Import jobs whose content was actually AI-processed (drives the import-ai cost line). */
  importAiJobs: z.number().int(),
  importBytes: z.number().int(),
  /** AI auditor clause examinations (internal_audit_ai_calls rows, success or failure). */
  internalAuditCalls: z.number().int(),
});
export type OrgUsageCounts = z.infer<typeof OrgUsageCountsSchema>;

export const UsageCostLineSchema = z.object({
  key: z.string(),
  label: z.string(),
  quantity: z.number(),
  unit: z.string(),
  /** Base infrastructure cost per unit, pence/month, before the multiplier. */
  unitCostPence: z.number(),
  costPence: z.number(),
});
export type UsageCostLine = z.infer<typeof UsageCostLineSchema>;

export const OrgUsageSchema = z.object({
  counts: OrgUsageCountsSchema,
  cost: z.object({
    lines: z.array(UsageCostLineSchema),
    /** Applied to infrastructure cost to produce the billable estimate. */
    multiplier: z.number(),
    subtotalPence: z.number(),
    totalPence: z.number(),
    currency: z.literal('GBP'),
  }),
  generatedAt: z.string(),
});
export type OrgUsage = z.infer<typeof OrgUsageSchema>;
